import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import Common from "us.common";
import { RootState } from "us.helper/types";
import { PlusOutlined } from "us.icons";
import { RatesTable, HistoryTable } from "./Components";
import * as Actions from "us.collection.admin/actions";
import { handleGoBack } from "us.helper/utility";
import { useNavigate } from "react-router-dom";
import { StandardRateDetails } from "./Components";
import {
  RATE_DRAWER_DEFAULT,
  RateActions,
} from "us.collection.admin/constants";
import { StandardRate } from "us.collection.admin/interfaces";
import "./Home.scss";
import { useSafeBackNavigation } from "us.helper/navigation";

const { $Affix, $PageHeader, $Divider, $Button, $Row, $Col, $Drawer } =
  Common.Components;

/**
 * @description - Standard Rates home component for combine child components.
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3479208007/View+Standard+Rate+UI+Implementation
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 03/08/2024
 */
const StandardRateHome: React.FC<PropsFromRedux> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSafeBackNavigation();

  const { drawerInfo, initStandardRates, manageDrawer } = props;

  useEffect(() => {
    initStandardRates && initStandardRates({});
  }, []);

  /**
   * @description Open the drawer by type
   * @param {RateActions} type - Type of the drawer
   * @param {StandardRate} record - Selected rate data
   */
  const openDrawer = (type: RateActions, record?: StandardRate) => {
    const { rateId = -1 } = record ?? {};
    // decide the suitable title for the drawer
    let title;

    if (type === RateActions.NEW) {
      title = t("US.COLLECTION.ADMIN:STANDARD_RATE.NEW_STANDARD_RATE");
    } else if (type === RateActions.EDIT) {
      title = t("US.COLLECTION.ADMIN:STANDARD_RATE.EDIT_STANDARD_RATE");
    } else {
      title = t("US.COLLECTION.ADMIN:STANDARD_RATE.DELETE_STANDARD_RATE");
    }

    // change drawer state
    manageDrawer &&
      manageDrawer({
        rateId,
        type,
        title,
      });
  };

  /**
   * @description - Close the drawer
   */
  const closeDrawer = () => {
    manageDrawer && manageDrawer(RATE_DRAWER_DEFAULT);
  };

  return (
    <>
      <$Affix offsetTop={48}>
        <div className="page-header header-border">
          <div className="d-flex align-items-center">
            <$PageHeader
              className="px-0"
              onBack={goBack}
              title={t("US.COLLECTION.ADMIN:STANDARD_RATE.STANDARD_RATE")}
            />
            <$Divider className="bui-devider" type="vertical" />
            <$Button
              data-testid="new-rate"
              className="mr-2"
              type="default"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => openDrawer(RateActions.NEW)}
            >
              {t("US.COLLECTION.ADMIN:STANDARD_RATE.ADD_NEW")}
            </$Button>
          </div>
        </div>
      </$Affix>

      <div className="space-content standard-rate">
        <div>
          <RatesTable
            onCallAction={(action, record) => openDrawer(action, record)}
          />
        </div>
        <div className="mt-5">
          <HistoryTable />
        </div>

        <$Drawer
          title={drawerInfo.title}
          width={"400px"}
          open={drawerInfo.visible}
          onClose={closeDrawer}
          destroyOnClose={true}
          maskClosable={false}
        >
          <StandardRateDetails onCancel={closeDrawer} />
        </$Drawer>
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { standardRates } = state;
  const { drawerInfo } = standardRates;
  return {
    drawerInfo,
  };
};

const { init } = Actions.standardRates;
const { get, manageDrawer } = init;

const mapDispatchToProps = {
  initStandardRates: get,
  manageDrawer,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(StandardRateHome);
